<template>
  <div class="user-behavior-container">
    <Card>
      <!-- 筛选区域 -->
      <div class="search-section">
        <Form ref="searchForm" :model="searchForm" inline :label-width="80">
          <FormItem label="用户ID">
            <Input v-model="searchForm.user_id" placeholder="请输入用户ID" clearable style="width: 200px" />
          </FormItem>
          <FormItem label="行为类型">
            <Select v-model="searchForm.action_type" placeholder="请选择行为类型" clearable style="width: 200px">
              <Option value="click">点击</Option>
              <Option value="page_view">页面浏览</Option>
              <Option value="scroll">滚动</Option>
              <Option value="input">输入</Option>
              <Option value="submit">提交</Option>
            </Select>
          </FormItem>
          <FormItem label="页面路径">
            <Input v-model="searchForm.page_path" placeholder="请输入页面路径" clearable style="width: 200px" />
          </FormItem>
          <FormItem label="时间范围">
            <DatePicker
              v-model="searchForm.date_range"
              type="datetimerange"
              format="yyyy-MM-dd HH:mm:ss"
              placeholder="选择时间范围"
              style="width: 350px"
            />
          </FormItem>
          <FormItem>
            <Button type="primary" @click="handleSearch">查询</Button>
            <Button @click="handleReset" style="margin-left: 8px">重置</Button>
            <Button type="success" @click="showStatistics = !showStatistics" style="margin-left: 8px">
              {{ showStatistics ? '隐藏统计' : '显示统计' }}
            </Button>
          </FormItem>
        </Form>
      </div>

      <!-- 统计图表区域 -->
      <div v-if="showStatistics" class="statistics-section">
        <Row :gutter="16">
          <Col span="8">
            <Card>
              <p slot="title">行为类型分布</p>
              <div ref="actionTypeChart" style="height: 300px"></div>
            </Card>
          </Col>
          <Col span="8">
            <Card>
              <p slot="title">页面访问统计</p>
              <div ref="pagePathChart" style="height: 300px"></div>
            </Card>
          </Col>
          <Col span="8">
            <Card>
              <p slot="title">时间趋势统计</p>
              <div ref="timeTrendChart" style="height: 300px"></div>
            </Card>
          </Col>
        </Row>
      </div>

      <!-- 列表区域 -->
      <div class="table-section" style="margin-top: 20px">
        <Table
          :columns="columns"
          :data="tableData"
          :loading="loading"
          stripe
          border
        >
          <template slot-scope="{ row }" slot="action_type">
            <Tag :color="getActionTypeColor(row.action_type)">{{ getActionTypeName(row.action_type) }}</Tag>
          </template>
          <template slot-scope="{ row }" slot="create_time">
            {{ formatDate(row.create_time) }}
          </template>
          <template slot-scope="{ row }" slot="action">
            <Button type="primary" size="small" @click="showDetail(row)">查看详情</Button>
          </template>
        </Table>

        <!-- 分页 -->
        <div style="margin: 10px; overflow: hidden">
          <div style="float: right;">
            <Page
              :total="pageTotal"
              :current="pageNum"
              :page-size="pageSize"
              :page-size-opts="[10, 20, 50, 100]"
              show-sizer
              show-total
              show-elevator
              @on-change="changePage"
              @on-page-size-change="handlePageSize"
            />
          </div>
        </div>
      </div>
    </Card>

    <!-- 详情弹窗 -->
    <Modal
      v-model="detailModal.visible"
      title="用户行为详情"
      width="800"
      :footer-hide="true"
    >
      <div v-if="detailModal.data" class="detail-content">
        <Row :gutter="16">
          <Col span="12">
            <div class="detail-item">
              <span class="detail-label">用户ID:</span>
              <span class="detail-value">{{ detailModal.data.user_id }}</span>
            </div>
          </Col>
          <Col span="12">
            <div class="detail-item">
              <span class="detail-label">行为类型:</span>
              <Tag :color="getActionTypeColor(detailModal.data.action_type)">
                {{ getActionTypeName(detailModal.data.action_type) }}
              </Tag>
            </div>
          </Col>
        </Row>
        <Row :gutter="16" style="margin-top: 16px">
          <Col span="12">
            <div class="detail-item">
              <span class="detail-label">页面路径:</span>
              <span class="detail-value">{{ detailModal.data.page_path }}</span>
            </div>
          </Col>
          <Col span="12">
            <div class="detail-item">
              <span class="detail-label">元素ID:</span>
              <span class="detail-value">{{ detailModal.data.element_id || '-' }}</span>
            </div>
          </Col>
        </Row>
        <Row :gutter="16" style="margin-top: 16px">
          <Col span="12">
            <div class="detail-item">
              <span class="detail-label">会话ID:</span>
              <span class="detail-value">{{ detailModal.data.session_id || '-' }}</span>
            </div>
          </Col>
          <Col span="12">
            <div class="detail-item">
              <span class="detail-label">操作时间:</span>
              <span class="detail-value">{{ formatDate(detailModal.data.create_time) }}</span>
            </div>
          </Col>
        </Row>

        <Divider>设备信息</Divider>
        <Row :gutter="16">
          <Col span="12">
            <div class="detail-item">
              <span class="detail-label">设备类型:</span>
              <span class="detail-value">{{ detailModal.data.device_type || '-' }}</span>
            </div>
          </Col>
          <Col span="12">
            <div class="detail-item">
              <span class="detail-label">浏览器:</span>
              <span class="detail-value">{{ detailModal.data.browser || '-' }}</span>
            </div>
          </Col>
        </Row>
        <Row :gutter="16" style="margin-top: 16px">
          <Col span="12">
            <div class="detail-item">
              <span class="detail-label">操作系统:</span>
              <span class="detail-value">{{ detailModal.data.os || '-' }}</span>
            </div>
          </Col>
          <Col span="12">
            <div class="detail-item">
              <span class="detail-label">IP地址:</span>
              <span class="detail-value">{{ detailModal.data.ip_address || '-' }}</span>
            </div>
          </Col>
        </Row>

        <Divider>额外数据</Divider>
        <div class="extra-data">
          <pre>{{ formatExtraData(detailModal.data.extra_data) }}</pre>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { getUserBehaviorList, getUserBehaviorStatistics } from '@/api/user-behavior'
import echarts from 'echarts'

export default {
  name: 'UserBehavior',
  data () {
    return {
      searchForm: {
        user_id: '',
        action_type: '',
        page_path: '',
        date_range: []
      },
      showStatistics: false,
      tableData: [],
      loading: false,
      pageNum: 1,
      pageSize: 10,
      pageTotal: 0,
      columns: [
        {
          title: '用户ID',
          key: 'user_id',
          width: 120
        },
        {
          title: '行为类型',
          slot: 'action_type',
          width: 120
        },
        {
          title: '页面路径',
          key: 'page_path',
          minWidth: 200,
          tooltip: true
        },
        {
          title: '元素ID',
          key: 'element_id',
          width: 150
        },
        {
          title: '操作时间',
          slot: 'create_time',
          width: 180
        },
        {
          title: '操作',
          slot: 'action',
          width: 120,
          align: 'center'
        }
      ],
      detailModal: {
        visible: false,
        data: null
      },
      charts: {
        actionTypeChart: null,
        pagePathChart: null,
        timeTrendChart: null
      }
    }
  },
  mounted () {
    this.getList()
  },
  beforeDestroy () {
    // 销毁图表实例
    Object.values(this.charts).forEach(chart => {
      if (chart) {
        chart.dispose()
      }
    })
  },
  methods: {
    // 获取列表数据
    async getList () {
      this.loading = true
      try {
        const params = {
          page: this.pageNum,
          limit: this.pageSize
        }

        // 添加筛选条件
        if (this.searchForm.user_id) {
          params.user_id = this.searchForm.user_id
        }
        if (this.searchForm.action_type) {
          params.action_type = this.searchForm.action_type
        }
        if (this.searchForm.page_path) {
          params.page_path = this.searchForm.page_path
        }
        if (this.searchForm.date_range && this.searchForm.date_range.length === 2) {
          params.start_time = this.formatDateTime(this.searchForm.date_range[0])
          params.end_time = this.formatDateTime(this.searchForm.date_range[1])
        }

        const res = await getUserBehaviorList(params)
        if (res.data) {
          this.tableData = res.data.results || []
          this.pageTotal = res.data.count || 0
        }
      } catch (error) {
        this.$Message.error('获取数据失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },

    // 获取统计数据
    async getStatistics () {
      try {
        const params = {}

        // 添加筛选条件
        if (this.searchForm.user_id) {
          params.user_id = this.searchForm.user_id
        }
        if (this.searchForm.date_range && this.searchForm.date_range.length === 2) {
          params.start_time = this.formatDateTime(this.searchForm.date_range[0])
          params.end_time = this.formatDateTime(this.searchForm.date_range[1])
        }

        const res = await getUserBehaviorStatistics(params)
        if (res.data) {
          this.$nextTick(() => {
            this.renderCharts(res.data)
          })
        }
      } catch (error) {
        this.$Message.error('获取统计数据失败: ' + (error.message || '未知错误'))
      }
    },

    // 渲染图表
    renderCharts (data) {
      // 行为类型分布图
      if (data.action_type_stats && this.$refs.actionTypeChart) {
        if (this.charts.actionTypeChart) {
          this.charts.actionTypeChart.dispose()
        }
        this.charts.actionTypeChart = echarts.init(this.$refs.actionTypeChart)
        const actionTypeOption = {
          tooltip: {
            trigger: 'item',
            formatter: '{b}: {c} ({d}%)'
          },
          legend: {
            orient: 'vertical',
            left: 'left'
          },
          series: [
            {
              name: '行为类型',
              type: 'pie',
              radius: '50%',
              data: Object.entries(data.action_type_stats).map(([key, value]) => ({
                name: this.getActionTypeName(key),
                value: value
              })),
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
              }
            }
          ]
        }
        this.charts.actionTypeChart.setOption(actionTypeOption)
      }

      // 页面访问统计图
      if (data.page_path_stats && this.$refs.pagePathChart) {
        if (this.charts.pagePathChart) {
          this.charts.pagePathChart.dispose()
        }
        this.charts.pagePathChart = echarts.init(this.$refs.pagePathChart)
        const pageStats = Object.entries(data.page_path_stats)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)

        const pagePathOption = {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow'
            }
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
          },
          xAxis: {
            type: 'value'
          },
          yAxis: {
            type: 'category',
            data: pageStats.map(item => item[0])
          },
          series: [
            {
              name: '访问次数',
              type: 'bar',
              data: pageStats.map(item => item[1])
            }
          ]
        }
        this.charts.pagePathChart.setOption(pagePathOption)
      }

      // 时间趋势图
      if (data.time_trend_stats && this.$refs.timeTrendChart) {
        if (this.charts.timeTrendChart) {
          this.charts.timeTrendChart.dispose()
        }
        this.charts.timeTrendChart = echarts.init(this.$refs.timeTrendChart)
        const timeTrendOption = {
          tooltip: {
            trigger: 'axis'
          },
          xAxis: {
            type: 'category',
            data: data.time_trend_stats.map(item => item.date)
          },
          yAxis: {
            type: 'value'
          },
          series: [
            {
              name: '行为次数',
              type: 'line',
              data: data.time_trend_stats.map(item => item.count),
              smooth: true,
              areaStyle: {}
            }
          ]
        }
        this.charts.timeTrendChart.setOption(timeTrendOption)
      }
    },

    // 查询
    handleSearch () {
      this.pageNum = 1
      this.getList()
      if (this.showStatistics) {
        this.getStatistics()
      }
    },

    // 重置
    handleReset () {
      this.searchForm = {
        user_id: '',
        action_type: '',
        page_path: '',
        date_range: []
      }
      this.pageNum = 1
      this.getList()
      if (this.showStatistics) {
        this.getStatistics()
      }
    },

    // 分页
    changePage (page) {
      this.pageNum = page
      this.getList()
    },

    handlePageSize (size) {
      this.pageSize = size
      this.pageNum = 1
      this.getList()
    },

    // 显示详情
    showDetail (row) {
      this.detailModal.data = row
      this.detailModal.visible = true
    },

    // 获取行为类型名称
    getActionTypeName (type) {
      const typeMap = {
        'click': '点击',
        'page_view': '页面浏览',
        'scroll': '滚动',
        'input': '输入',
        'submit': '提交'
      }
      return typeMap[type] || type
    },

    // 获取行为类型颜色
    getActionTypeColor (type) {
      const colorMap = {
        'click': 'blue',
        'page_view': 'green',
        'scroll': 'orange',
        'input': 'purple',
        'submit': 'red'
      }
      return colorMap[type] || 'default'
    },

    // 格式化日期
    formatDate (dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    // 格式化日期时间用于请求参数
    formatDateTime (date) {
      if (!date) return ''
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    // 格式化额外数据
    formatExtraData (data) {
      if (!data) return '无额外数据'
      if (typeof data === 'string') {
        try {
          return JSON.stringify(JSON.parse(data), null, 2)
        } catch (e) {
          return data
        }
      }
      return JSON.stringify(data, null, 2)
    }
  },
  watch: {
    showStatistics (val) {
      if (val) {
        this.getStatistics()
      }
    }
  }
}
</script>

<style scoped>
.user-behavior-container {
  padding: 20px;
}

.search-section {
  margin-bottom: 20px;
}

.statistics-section {
  margin-bottom: 20px;
}

.table-section {
  margin-top: 20px;
}

.detail-content {
  padding: 10px;
}

.detail-item {
  margin-bottom: 12px;
}

.detail-label {
  font-weight: bold;
  color: #333;
  margin-right: 8px;
}

.detail-value {
  color: #666;
}

.extra-data {
  background-color: #f5f7fa;
  padding: 12px;
  border-radius: 4px;
  max-height: 400px;
  overflow-y: auto;
}

.extra-data pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
}
</style>
